import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserToken, UserRoleToken } from '@sleed/bizcommerce-api';
import { usersApi, userRolesApi } from '~/lib/api/client';
import { formToToken, type UserFormValues } from '~/lib/schemas/user';

export const userKeys = {
	all: ['users'] as const,
	list: () => [...userKeys.all, 'list'] as const,
	roles: () => [...userKeys.all, 'roles'] as const
};

/** Full user list (legacy `/users/getall`, loadMode 'raw' — all rows, no server paging). */
export function useUsersList() {
	return useQuery<UserToken[]>({
		queryKey: userKeys.list(),
		queryFn: async () => (await usersApi.getAll()).data.items ?? []
	});
}

/** User-role lookup ({id, nm}) for the role column + edit dropdown. */
export function useUserRoles() {
	return useQuery<UserRoleToken[]>({
		queryKey: userKeys.roles(),
		queryFn: async () => (await userRolesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

/** Create / update / delete a user (parity with the legacy CustomStore insert/update/remove). */
export function useUserMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: userKeys.list() });

	const create = useMutation({
		mutationFn: async (form: UserFormValues) => (await usersApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async (form: UserFormValues) => (await usersApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await usersApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
